import { useCallback, useEffect, useState } from "react";
import { Smartphone } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ApkAndroid() {
  const [apkInfo, setApkInfo] = useState(null);
  const [apkArchivo, setApkArchivo] = useState(null);
  const [apkVersionCode, setApkVersionCode] = useState("1004");
  const [apkVersionName, setApkVersionName] = useState("1.004");
  const [subiendoApk, setSubiendoApk] = useState(false);

  const cargarApk = useCallback(async () => {
    try {
      const { data } = await api.get("/apk/version");
      setApkInfo(data);
      if (data?.version_code) setApkVersionCode(String(data.version_code + 1));
      if (data?.version_name) setApkVersionName(data.version_name);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    cargarApk();
  }, [cargarApk]);

  const publicarApk = async () => {
    if (!apkArchivo) {
      toast.error("Selecciona un archivo .apk");
      return;
    }
    const code = parseInt(apkVersionCode, 10);
    if (!code || code < 1) {
      toast.error("El version_code debe ser un número entero");
      return;
    }
    const fd = new FormData();
    fd.append("archivo", apkArchivo);
    fd.append("version_code", String(code));
    fd.append("version_name", apkVersionName.trim());
    setSubiendoApk(true);
    try {
      const { data } = await api.post("/apk/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setApkInfo(data);
      setApkArchivo(null);
      toast.success(`APK ${data.version_name || data.version_code} publicada`);
      if (data?.version_code) setApkVersionCode(String(data.version_code + 1));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubiendoApk(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6" data-testid="apk-android-page">
      <PageHeader
        icon={Smartphone}
        title="Aplicación Android"
        subtitle="Publica la APK para que los móviles detecten versión nueva al abrir la app"
        sectionKey="apk"
      />

      <div className="rounded-xl border border-indigo-100 bg-white p-6 shadow-sm space-y-4">
        {apkInfo?.disponible ? (
          <p className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            Publicada: <b>{apkInfo.version_name || "—"}</b> (código {apkInfo.version_code})
            {apkInfo.size_bytes ? ` · ${(apkInfo.size_bytes / 1024 / 1024).toFixed(1)} MB` : ""}
          </p>
        ) : (
          <p className="rounded-lg border border-zinc-100 bg-zinc-50 px-3 py-2 text-sm text-zinc-500">
            Todavía no hay ninguna APK publicada. Sube VenturaHR-1.004.apk (o posterior) para que los
            teléfonos puedan actualizarse.
          </p>
        )}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label>version_code (entero, debe ser mayor que la instalada)</Label>
            <Input
              value={apkVersionCode}
              onChange={(e) => setApkVersionCode(e.target.value)}
              data-testid="apk-version-code"
            />
          </div>
          <div className="space-y-1.5">
            <Label>version_name (p. ej. 1.004)</Label>
            <Input
              value={apkVersionName}
              onChange={(e) => setApkVersionName(e.target.value)}
              data-testid="apk-version-name"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Archivo .apk</Label>
          <Input
            type="file"
            accept=".apk,application/vnd.android.package-archive"
            onChange={(e) => setApkArchivo(e.target.files?.[0] || null)}
            data-testid="apk-file-input"
          />
          {apkArchivo && <p className="text-xs text-zinc-500">{apkArchivo.name}</p>}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={publicarApk}
            disabled={subiendoApk || !apkArchivo}
            className="rounded-lg bg-indigo-600 hover:bg-indigo-700"
            data-testid="apk-publicar-btn"
          >
            {subiendoApk ? "Subiendo…" : "Publicar APK"}
          </Button>
          <Button type="button" variant="outline" onClick={cargarApk} className="rounded-lg">
            Actualizar estado
          </Button>
        </div>

        <p className="text-xs text-zinc-400">
          Solo el súper administrador puede publicar versiones. Al abrir la app, si el código de versión
          del servidor es mayor, el empleado ve un aviso para descargar e instalar. Requiere permitir
          instalación desde esta fuente en Android.
        </p>
      </div>
    </div>
  );
}
